import {
  Building2,
  FolderOpen,
  GraduationCap,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  PanelsTopLeft,
  ScrollText,
  UserRound,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { NavLink, useNavigate } from "react-router";

import type { Me, StaffMe } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import type { Permission } from "@/auth/AuthProvider";
import { ThemeToggle } from "@/shell/ThemeToggle";
import { Avatar } from "@/ui/Avatar";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Menu, MenuItem, MenuLabel, MenuSeparator } from "@/ui/Menu";

type NavItem = { to: string; label: string; icon: LucideIcon; needs?: Permission };

const NAV: readonly NavItem[] = [
  { to: "/sets", label: "Sets", icon: FolderOpen },
  { to: "/knowledge", label: "Knowledge", icon: GraduationCap },
  { to: "/members", label: "Members", icon: Users, needs: "members:manage" },
  { to: "/organization", label: "Organization", icon: Building2, needs: "org:licence:read" },
  { to: "/audit", label: "Audit log", icon: ScrollText, needs: "audit:read" },
];

/** The platform console's pages, for Darkstar staff (ADR-0015). */
const STAFF_NAV: readonly NavItem[] = [
  { to: "/platform/orgs", label: "Organizations", icon: Building2 },
];

const ROLE_LABEL: Record<Me["role"], string> = {
  owner: "Owner",
  admin: "Admin",
  estimator: "Estimator",
  viewer: "Viewer",
  staff_viewer: "Darkstar staff (read-only)",
};

interface Props {
  /** A member's sidebar, or Darkstar staff's in the platform console. */
  me: Me | StaffMe;
  collapsed: boolean;
  /** Absent in the phone drawer, and where the page holds the sidebar collapsed. */
  onToggle?: () => void;
  /** Called after a link is followed, so the phone drawer can close. */
  onNavigate?: () => void;
}

/**
 * The app's frame on the left: whose workspace this is, where to go, and who is signed
 * in. Collapsed, it keeps its icons and names them on hover.
 */
export function Sidebar({ me, collapsed, onToggle, onNavigate }: Props) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const staff = me.kind === "staff";
  const heading = staff ? "Darkstar platform" : me.organization.name;
  const nav = staff
    ? STAFF_NAV
    : NAV.filter((item) => !item.needs || me.permissions.includes(item.needs));
  const roleLabel = staff ? "Darkstar staff" : ROLE_LABEL[me.role];
  const who = me.user.name || me.user.email;

  const leave = async () => {
    await signOut();
    void navigate("/sign-in", { replace: true });
  };

  return (
    <div
      className={cn(
        "flex h-full flex-col border-r border-line bg-surface",
        collapsed ? "w-14" : "w-60",
      )}
      data-collapsed={collapsed}
    >
      <div
        className={cn(
          "flex h-14 shrink-0 items-center gap-2.5 border-b border-line",
          collapsed ? "justify-center" : "px-3",
        )}
      >
        <span
          aria-hidden="true"
          className="grid size-8 shrink-0 place-items-center rounded-md bg-accent text-accent-ink"
        >
          <PanelsTopLeft className="size-4" />
        </span>
        {collapsed ? null : (
          <div className="min-w-0 leading-tight">
            <p className="truncate text-sm font-semibold text-ink">Unit Take-Off</p>
            <p className="truncate text-xs text-muted" title={heading}>
              {heading}
            </p>
          </div>
        )}
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-0.5">
          {nav.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                onClick={onNavigate}
                title={collapsed ? label : undefined}
                aria-label={collapsed ? label : undefined}
                className={({ isActive }) =>
                  cn(
                    "flex h-9 items-center gap-2.5 rounded-md text-sm no-underline",
                    collapsed ? "justify-center" : "px-2.5",
                    isActive
                      ? "bg-accent-soft font-medium text-accent"
                      : "text-ink-2 hover:bg-sunken hover:text-ink",
                  )
                }
              >
                <Icon aria-hidden="true" className="size-4 shrink-0" />
                {collapsed ? null : <span className="truncate">{label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div
        className={cn(
          "flex items-center gap-2 border-t border-line p-2",
          collapsed ? "flex-col" : "justify-between",
        )}
      >
        <Menu
          align="start"
          trigger={
            <button
              type="button"
              aria-label={`Account: ${who}`}
              title={collapsed ? who : undefined}
              className={cn(
                "flex min-w-0 items-center gap-2 rounded-md p-1 text-left hover:bg-sunken",
                collapsed ? "" : "flex-1",
              )}
            >
              <Avatar name={who} className="size-7" />
              {collapsed ? null : (
                <span className="min-w-0 leading-tight">
                  <span className="block truncate text-xs font-medium text-ink">{who}</span>
                  <span className="block truncate text-xs text-muted">{roleLabel}</span>
                </span>
              )}
            </button>
          }
        >
          <MenuLabel>
            <span className="block truncate font-medium text-ink">{who}</span>
            <span className="block truncate">{me.user.email}</span>
          </MenuLabel>
          <MenuSeparator />
          {staff || me.staff_view ? null : (
            <MenuItem
              onSelect={() => {
                onNavigate?.();
                void navigate("/profile");
              }}
            >
              <UserRound aria-hidden="true" />
              Profile
            </MenuItem>
          )}
          <MenuItem onSelect={() => void leave()}>
            <LogOut aria-hidden="true" />
            Sign out
          </MenuItem>
        </Menu>
        <div className={cn("flex items-center gap-1", collapsed ? "flex-col" : "")}>
          <ThemeToggle />
          {onToggle ? (
            <Button
              variant="ghost"
              size="sm"
              className="w-7 px-0"
              aria-label={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
              title={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
              onClick={onToggle}
            >
              {collapsed ? (
                <PanelLeftOpen aria-hidden="true" />
              ) : (
                <PanelLeftClose aria-hidden="true" />
              )}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
